import type { RoadsAndSewersSumAnalysis } from "../hooks/useRoadsAndSewersTotalSync";

export interface RoadsAndSewersSumAnalysisRow {
  label: string;
  amount: number;
}

/**
 * The Roads & Sewers Sum Analysis Bill Summary's per-domain rows, matching
 * `RsSumAnalysisBillSummary.tsx`'s on-screen `sections` exactly: each
 * domain's ad-hoc extra items fold straight into that domain's own row
 * (never a separate "(Extra Items)" row), dynamic Bill Summary tabs appended
 * after, zero-amount domains dropped. Shared by every export that needs this
 * exact breakdown (`ReviewCalculate.tsx`'s BOQ "Sum Analysis" sheet,
 * `RunMatrix.tsx`'s export "Summary" sheet) so a third hand-copy of this list
 * can't silently drift from the other two — see the "dual total source"
 * gotchas already documented for this feature in `knowledge_graph.md` §4.
 */
export const buildRoadsAndSewersSumAnalysisRows = (
  totals: RoadsAndSewersSumAnalysis,
  weholiteSupplierName: string,
): RoadsAndSewersSumAnalysisRow[] => {
  const rows: RoadsAndSewersSumAnalysisRow[] = [
    { label: "Preliminary Items", amount: totals.prelimsTotal + totals.prelimsExtraItemsTotal },
    { label: "Foul Sewers", amount: totals.foulTotal + totals.foulSewersExtraItemsTotal },
    { label: "Pump Station", amount: totals.pumpStationTotal + totals.pumpStationExtraItemsTotal },
    { label: "Rising Main", amount: totals.risingMainTotal + totals.risingMainExtraItemsTotal },
    { label: "Storm Sewers", amount: totals.stormTotal + totals.stormSewersExtraItemsTotal },
    { label: "Ponds & Swales", amount: totals.pondsSwalesTotal + totals.pondsSwalesExtraItemsTotal },
    { label: "Cellular Attenuation", amount: totals.cellularAttenuationTotal + totals.cellularAttenuationExtraItemsTotal },
    { label: `${weholiteSupplierName} Attenuation`, amount: totals.weholiteAttenuationTotal + totals.weholiteAttenuationExtraItemsTotal },
    { label: "Conc Attenuation", amount: totals.concAttenuationTotal + totals.concAttenuationExtraItemsTotal },
    { label: "Box Culverts", amount: totals.boxCulvertsTotal + totals.boxCulvertsExtraItemsTotal },
    { label: "Roads", amount: totals.roadsTotal + totals.roadsExtraItemsTotal },
    { label: "Main Services", amount: totals.mainServicesTotal + totals.mainServicesExtraItemsTotal },
    { label: "Substation", amount: totals.substationTotal + totals.substationExtraItemsTotal },
    { label: "POS", amount: totals.posTotal + totals.posExtraItemsTotal },
    { label: "Compound", amount: totals.compoundTotal + totals.compoundExtraItemsTotal },
  ].filter((row) => row.amount !== 0);

  totals.dynamicBillSummaryTabTotals.forEach((tab) => {
    rows.push({ label: tab.label, amount: tab.amount });
  });

  return rows;
};
